// support multiple carousel instances on the same page
class BackdropCarousel {
  static instances = [];
  static instanceByContainer = new WeakMap();
  static resizeTimeout;
  static globalEventsBound = false;

  constructor(container) {
    this.container = container;
    this.backdropCarousel = container;
    this.backdrop = container.querySelector('.mcl-backdrop');
    this.track = container.querySelector('.track');
    this.cards = container.querySelectorAll('.mcl-carousel .card');
    this.cardFooters = container.querySelectorAll('.mcl-carousel .card .footer');
    this.cardImages = container.querySelectorAll('.mcl-carousel .card img');
    this.start = container.querySelector('.mcl-carousel .arrow.start');
    this.prev = container.querySelector('.mcl-carousel .arrow.left');
    this.next = container.querySelector('.mcl-carousel .arrow.right');
    this.forwardIcon = container.querySelector('.mcl-carousel .arrow-forward-svg');
    this.cardForwardIcons = container.querySelectorAll('.mcl-carousel .card-arrow-forward-svg');
    this.nextIcon = container.querySelector('.mcl-carousel .arrow-right-svg');
    this.prevIcon = container.querySelector('.mcl-carousel .arrow-left-svg');
    this.carousel = container.querySelector('.mcl-carousel');
    this.dotsContainer = container.querySelector('.mcl-carousel .dots');

    this.gap = 13;
    this.index = 0;
    this.isMobile = false;
    this.isTouch = false;
    this.offset = 0;
    this.carouselX = 0;
    this.carouselY = 0;
    this.coverCardWidth = 0;
    this.currentViewCardWidth = 0;
    this.currentViewCardHeight = 0;
    this.currentViewCardCount = 0;
    this.carouselTotalHeight = 0;
    this.dotsCount = 0;
    this.touchListenersBound = false;

    // drag/touch tracking
    this.touchStartX = 0;
    this.touchCurrentX = 0;
    this.isDragging = false;

    // validate essential elements
    if (!this.cards.length || !this.track || !this.carousel || !this.dotsContainer) {
      console.warn('BackdropCarousel: missing required elements in container', container);
      return;
    }

    this.init();
    this.addListeners();
    this.syncTouchListeners();
    BackdropCarousel.instances.push(this);
    BackdropCarousel.instanceByContainer.set(container, this);
  }

  init() {
    if (!this.cards || !this.cards.length) return;

    this.isMobile = window.innerWidth < 768;
    this.isTouch = this.isTouchDevice();
    this.offset = this.isMobile ? this.gap : 50;
    this.carouselX = this.isMobile ? 2 : 50;
    this.carouselY = this.isMobile ? 55 : 0;

    const coverWidth = this.cards[0].offsetWidth;
    const firstCardWidth = this.cards[1] ? (this.cards[1].offsetWidth > this.cards[0].offsetWidth ? this.cards[1].offsetWidth : this.cards[0].offsetWidth) : this.cards[0].offsetWidth;
    const firstCardHeight = this.cards[0].offsetHeight;

    // In theme editor, section DOM can be temporarily measured at zero during rerender.
    if (!firstCardWidth || !firstCardHeight) {
      return;
    }

    this.coverCardWidth = coverWidth + this.gap;
    this.currentViewCardWidth = firstCardWidth + this.gap;
    this.currentViewCardHeight = firstCardHeight;
    this.currentViewCardCount = Math.max(1, window.innerWidth / this.currentViewCardWidth);
    this.dotsCount = Math.max(1, 1 + (this.cards.length - Math.floor(this.currentViewCardCount)));

    // backdrop
    if (this.backdrop) {
      this.backdrop.style.height = `${this.currentViewCardHeight * 1.25}px`;
    }

    // initial carousel position
    if ((this.index === 0 && !this.isMobile) || this.isMobile) {
      if (this.carousel) {
        this.carousel.style.transform = `translate(${this.carouselX}%, ${this.carouselY}%)`;
      }
    }

    // SVG icons sizing
    if (this.forwardIcon) {
      this.forwardIcon.setAttribute('width', `${this.coverCardWidth * 0.13}px`);
      this.forwardIcon.setAttribute('height', `${this.coverCardWidth * 0.13}px`);
    }
    if (this.nextIcon) {
      this.nextIcon.setAttribute('width', `${this.coverCardWidth * 0.09}px`);
      this.nextIcon.setAttribute('height', `${this.coverCardWidth * 0.09}px`);
    }
    if (this.prevIcon) {
      this.prevIcon.setAttribute('width', `${this.coverCardWidth * 0.09}px`);
      this.prevIcon.setAttribute('height', `${this.coverCardWidth * 0.09}px`);
    }

    // arrow buttons sizing
    if (this.next) {
      this.next.style.width = `${this.coverCardWidth * 0.122}px`;
      this.next.style.height = `${this.coverCardWidth * 0.122}px`;
      this.next.style.padding = `${Math.round((parseFloat(this.next.style.height) - parseFloat(this.nextIcon.getAttribute('height'))) / 2)}px`;
    }
    if (this.prev) {
      this.prev.style.width = `${this.coverCardWidth * 0.122}px`;
      this.prev.style.height = `${this.coverCardWidth * 0.122}px`;
      this.prev.style.padding = `${Math.round((parseFloat(this.prev.style.height) - parseFloat(this.prevIcon.getAttribute('height'))) / 2)}px`;
    }

    // card forward icons
    this.cardForwardIcons.forEach(svg => {
      svg.setAttribute('width', `${this.coverCardWidth * 0.08}px`);
      svg.setAttribute('height', `${this.coverCardWidth * 0.08}px`);
      svg.style.padding = `${svg.offsetWidth * 0.12}px`;
    });

    // card footer and image adjustments
    this.cardFooters.forEach(footer => {
      footer.style.width = `${this.currentViewCardWidth - this.gap}px`;
    });
    this.cardImages.forEach(image => {
      if (this.currentViewCardHeight >= window.innerHeight) {
        image.style.aspectRatio = '1/1';
        image.style.height = `${window.innerHeight * 0.85}px`;
      } else {
        image.style.aspectRatio = '3/4';
      }
    });

    // build dots
    if (this.dotsContainer) {
      this.dotsContainer.innerHTML = '';
      for (let i = 0; i < this.dotsCount; i++) {
        const dot = document.createElement('button');
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        dot.addEventListener('click', () => {
          this.index = i;
          this.updateCarousel();
        });
        this.dotsContainer.appendChild(dot);
      }
    }

    // mobile padding fix
    this.carouselTotalHeight = Math.round(
      this.carousel.offsetHeight +
      (this.backdrop ? this.backdrop.offsetHeight * (this.carouselY / 100) : 0)
    );
    if (this.isMobile) {
      const backdropHeight = this.backdrop ? this.backdrop.offsetHeight : 0;
      if (this.carouselTotalHeight > backdropHeight) {
        this.backdropCarousel.style.paddingBottom = `${this.carouselTotalHeight - backdropHeight}px`;
      } else {
        this.backdropCarousel.style.paddingBottom = '0px';
      }
    } else {
      this.backdropCarousel.style.paddingBottom = '0px';
    }
  }

  addListeners() {
    // start button (intro card)
    if (this.start) {
      this.onStartClick = () => this.advanceFromIntro();
      this.start.addEventListener('click', this.onStartClick);
    }

    // next button
    if (this.next) {
      this.onNextClick = () => this.advanceCarousel();
      this.next.addEventListener('click', this.onNextClick);
    }

    // prev button
    if (this.prev) {
      this.onPrevClick = () => this.goBackCarousel();
      this.prev.addEventListener('click', this.onPrevClick);
    }

    // add touch listeners for drag/swipe on mobile
    if (this.isMobile) {
      this.addTouchListeners();
    }
  }

  advanceFromIntro() {
    if (this.carousel) this.carousel.style.transform = `translate(0%, ${this.carouselY}%)`;
    if (this.prev) this.prev.style.display = 'block';
    if (this.next) this.next.style.display = 'block';
    this.index++;
    this.updateCarousel();
    if (!this.isMobile && this.backdrop) {
      this.backdrop.style.transform = `translateX(-${this.backdrop.offsetWidth * 0.75}px)`;
    }

  }

  advanceCarousel() {
    if (this.index === 0) {
      this.advanceFromIntro();
    } else if (this.index + this.currentViewCardCount - 1 < this.cards.length - 1) {
      this.index++;
      this.updateCarousel();
    }
  }

  goBackCarousel() {
    if (this.index === 0 || (this.isMobile && this.index === 1)) {
      if (this.backdrop) this.backdrop.style.transform = 'translateX(0)';
      if (this.carousel) this.carousel.style.transform = `translate(${this.carouselX}%, ${this.carouselY}%)`;
      if (this.prev) this.prev.style.display = 'none';
      if (this.next) this.next.style.display = 'none';

    }
    if (this.index > 0 || (this.isMobile && this.index > 1)) {
      this.index--;
      this.updateCarousel();
    }
  }

  addTouchListeners() {
    if (this.touchListenersBound || !this.container) return;
    const viewport = this.container.querySelector('.viewport');
    if (!viewport) return;

    const minSwipeDistance = 40; // pixels - reduced for faster responsiveness

    this.onTouchStart = (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchCurrentX = this.touchStartX;
      this.isDragging = true;
      // Remove transition during drag for real-time feedback
      this.track.style.transition = 'none';
    };

    this.onTouchMove = (e) => {
      if (!this.isDragging) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const dx = this.touchStartX - currentX;
      const dy = currentY - this.touchStartY;

      // Only apply drag if horizontal movement dominates vertical
      if (Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault(); // Block vertical scroll
        const cardWidth = this.cards[0].offsetWidth + this.gap;
        const base = this.offset - this.index * cardWidth;

        // edge locking: prevent overshoot past first/last slide
        let moveX = base - dx;
        const maxIndex = Math.max(0, this.cards.length - this.currentViewCardCount);
        if (this.index === 0 && dx < 0) {
          // dragging right on first slide
          moveX = base;
        }
        if (this.index >= maxIndex && dx > 0) {
          // dragging left on last slide
          moveX = base;
        }

        this.track.style.transform = `translateX(${moveX}px)`;
        this.touchCurrentX = currentX;
      }
    };

    this.onTouchEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;

      // Restore smooth transition for snap
      this.track.style.transition = 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';

      const swipeDistance = this.touchStartX - this.touchCurrentX;

      // swipe left (next)
      if (swipeDistance > minSwipeDistance) {
        this.advanceCarousel();
      }
      // swipe right (prev) 
      else if (swipeDistance < -minSwipeDistance) {
        this.goBackCarousel();
      } else {
        // Snap back if not enough distance
        this.updateCarousel();
      }

      this.touchStartX = 0;
      this.touchCurrentX = 0;
      this.touchStartY = 0;
    };

    viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
    viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.touchListenersBound = true;
  }

  removeTouchListeners() {
    if (!this.touchListenersBound || !this.container) return;
    const viewport = this.container.querySelector('.viewport');
    if (!viewport) return;

    viewport.removeEventListener('touchstart', this.onTouchStart);
    viewport.removeEventListener('touchmove', this.onTouchMove);
    viewport.removeEventListener('touchend', this.onTouchEnd);
    this.touchListenersBound = false;
  }

  syncTouchListeners() {
    if (this.isMobile) {
      this.addTouchListeners();
    } else {
      this.removeTouchListeners();
    }
  }

  static setupGlobalResize() {
    if (BackdropCarousel.globalEventsBound) return;
    BackdropCarousel.globalEventsBound = true;

    window.addEventListener('resize', () => {
      clearTimeout(BackdropCarousel.resizeTimeout);
      BackdropCarousel.resizeTimeout = setTimeout(() => {
        BackdropCarousel.instances.forEach(instance => instance.handleResize());
      }, 150);
    });

    document.addEventListener('shopify:section:load', (event) => {
      BackdropCarousel.initInRoot(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      BackdropCarousel.destroyInRoot(event.target);
    });
  }

  static initInRoot(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    const containers =
      scope.matches && scope.matches('.mcl-backdrop-carousel')
        ? [scope]
        : scope.querySelectorAll('.mcl-backdrop-carousel');

    containers.forEach((container) => {
      if (BackdropCarousel.instanceByContainer.has(container)) return;
      new BackdropCarousel(container);
    });
  }

  static destroyInRoot(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    const containers =
      scope.matches && scope.matches('.mcl-backdrop-carousel')
        ? [scope]
        : scope.querySelectorAll('.mcl-backdrop-carousel');

    containers.forEach((container) => {
      const instance = BackdropCarousel.instanceByContainer.get(container);
      if (instance) {
        instance.destroy();
      }
    });
  }

  handleResize() {
    // Check if container is still in the DOM before proceeding
    if (!this.container || !this.container.isConnected) {
      this.destroy();
      return;
    }

    this.init();
    if (this.index > 0 && !this.isMobile && this.backdrop) {
      this.backdrop.style.transform = `translateX(-${this.backdrop.offsetWidth * 0.75}px)`;
      if (this.carousel) this.carousel.style.transform = `translate(0, ${this.carouselY}%)`;
    }
    if (this.index > 0 && this.isMobile && this.backdrop) {
      this.backdrop.style.transform = 'translateX(0px)';
    }
    if (this.index === 0 && !this.isMobile && this.backdrop) {
      this.backdrop.style.transform = 'translateX(0px)';
    }
    if (!this.isMobile) {
      this.backdropCarousel.style.paddingBottom = '0px';

    } else {
      this.carouselTotalHeight = Math.round(
        this.carousel.offsetHeight +
        (this.backdrop ? this.backdrop.offsetHeight * (this.carouselY / 100) : 0)
      );
      const backdropHeight = this.backdrop ? this.backdrop.offsetHeight : 0;
      this.backdropCarousel.style.paddingBottom =
        this.carouselTotalHeight > backdropHeight
          ? `${this.carouselTotalHeight - backdropHeight}px`
          : '0px';
    }

    this.syncTouchListeners();
  }

  destroy() {
    // Remove event listeners
    if (this.start && this.onStartClick) {
      this.start.removeEventListener('click', this.onStartClick);
    }
    if (this.next && this.onNextClick) {
      this.next.removeEventListener('click', this.onNextClick);
    }
    if (this.prev && this.onPrevClick) {
      this.prev.removeEventListener('click', this.onPrevClick);
    }

    // Remove touch listeners from viewport
    this.removeTouchListeners();

    // Remove this instance from the static array
    const index = BackdropCarousel.instances.indexOf(this);
    if (index > -1) {
      BackdropCarousel.instances.splice(index, 1);
    }

    if (this.container) {
      BackdropCarousel.instanceByContainer.delete(this.container);
    }

    // Clear all references
    this.container = null;
    this.backdropCarousel = null;
    this.backdrop = null;
    this.track = null;
    this.cards = null;
    this.cardFooters = null;
    this.cardImages = null;
    this.start = null;
    this.prev = null;
    this.next = null;
  }

  updateDots() {
    if (!this.dotsContainer) return;
    [...this.dotsContainer.children].forEach((dot, i) => {
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });

    const isNarrowNonTouch = this.isMobile && !this.isTouch; // narrow desktop
    const hideControls = this.isMobile && this.isTouch; // true mobile

    // detect if carousel is at full width (translate(0))
    const carouselAtFullWidth = this.carousel &&
      this.carousel.style &&
      this.carousel.style.transform &&
      this.carousel.style.transform.indexOf('translate(0') !== -1;

    // next button visibility and opacity
    if (this.next) {
      if (hideControls) {
        this.next.style.display = 'none';
        this.next.style.opacity = '';
      } else {
        // in mobile: hide next when intro card visible (index === 0) OR at last dot
        // in desktop: hide next when at last dot
        let nextisHidden = false;
        if (this.index === this.dotsCount - 1) {
          nextisHidden = true;
        } else if (this.isMobile && this.index === 0) {
          nextisHidden = true;
        }
        this.next.style.display = nextisHidden ? 'none' : 'block';
        this.next.style.opacity = isNarrowNonTouch ? '0.3' : '';
      }
    }

    // prev button visibility and opacity
    if (this.prev) {
      if (hideControls) {
        this.prev.style.display = 'none';
        this.prev.style.opacity = '';
      } else {
        // show prev if index > 0, or special case: desktop with intro visible but carousel at full width
        const showPrev = this.index > 0 || (!this.isMobile && this.index === 0 && carouselAtFullWidth);
        this.prev.style.display = showPrev ? 'block' : 'none';
        this.prev.style.opacity = isNarrowNonTouch ? '0.3' : '';
      }
    }
  }

  // feature-detect a touch-capable device
  isTouchDevice() {
    return (
      ('ontouchstart' in window) ||
      ((navigator.maxTouchPoints || 0) > 0) ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
    );
  }

  updateCarousel() {
    const cardWidth = this.cards[0].offsetWidth + this.gap;
    if (this.track) {
      this.track.style.transform = `translateX(${this.offset - this.index * cardWidth}px)`;
    }
    this.updateDots();
  }
}

// wait for DOM to be ready, then initialize all instances
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    BackdropCarousel.initInRoot(document);
    BackdropCarousel.setupGlobalResize();
  });
} else {
  BackdropCarousel.initInRoot(document);
  BackdropCarousel.setupGlobalResize();
}
